<!-- eslint-disable max-len -->
<template>
  <div style="margin-bottom: 20px;">
    <collapsible-section
      :id="collapsibleId"
      :name="`TTS (${truncatedText})`"
      :buttonStyle="{
        'font-size': '120%'
      }"
    >
      <div v-if="voiceLine && voiceLine.recordedText && existingVoiceLine" style="font-style: italic; font-size: 80%;">
        Currently recorded line: "{{ voiceLine.recordedText }}"</div>
      <div style="display: flex; flex-flow: row wrap;">
        <button v-if="existingVoiceLine" class="btn" @click.prevent="previewVoiceLine" style="padding: 0 5px;">
          Preview Voice Line
        </button>
        <button
          v-if="existingVoiceLine"
          class="btn"
          @click.prevent="downloadVoiceLine"
          style="
            padding: 0 5px;
            margin-left: 4px;
          "
        >
          Download
        </button>
        <button
          v-if="character"
          class="btn"
          @click.prevent="generateButtonClicked"
          style="
            padding: 0 5px;
            margin-left: 4px;
          "
          :disabled="!text || !text.trim()"
        >
          {{ existingVoiceLine ? 'Regenerate ' : 'Generate' }} Voice Line <template v-if="!text || !text.trim()"> (no
            text to generate)</template>
        </button>
        <button
          v-if="existingVoiceLine"
          class="btn"
          @click.prevent="deleteExistingVoiceLine"
          style="
            padding: 0 5px;
            margin-left: 4px;
          "
        >
          Delete Voice Line
        </button>
      </div>
      <asset-select-input label v-model="customVoiceLine" @input="onCustomVoiceLineAssetSelected">
        Custom Voice Line
      </asset-select-input>
      <div v-if="existingVoiceLine" style="display: flex; flex-flow: row wrap;">
        <checkbox-input
          v-model="voiceLine.preventBulkGenerationUpdating"
          style="
            margin: 0 18px 0 5px;
          "
          @input="onInputPreventBulkGenerationUpdating"
        >Prevent Bulk Generation Overwriting</checkbox-input>
        <checkbox-input
          v-model="voiceLine.preventBulkGenerationUpdatingVoice"
          style="
            margin: 0 0 0 5px;
          "
          @input="onInputPreventBulkGenerationUpdatingVoice"
        >Prevent Bulk Generation Changing Voice Settings (only overwrites text changes)</checkbox-input>
      </div>
      <template v-if="!customVoiceLine">
        <voice-config-editor :config="voiceLine" :character="character"></voice-config-editor>
        <div style="display: flex; flex-flow: row wrap;">
          <button class="btn" @click.prevent="generateButtonClicked" style="padding: 0 5px;"
            :disabled="!text || !text.trim()">
            {{ existingVoiceLine ? 'Regenerate ' : 'Generate' }} Voice Line <template v-if="!text || !text.trim()"> (no
              text)</template>
          </button>
        </div>
        <div
          v-if="text"
          style="
            font-style: italic;
            font-size: 80%;
            margin-bottom: 8px;
          "
        >Will generate the line: "{{ filteredText }}"</div>
      </template>
    </collapsible-section>
  </div>
</template>

<script>
import {
  mapMutations,
  mapActions,
} from 'vuex';
import VoiceConfigEditor from '../../blade/pages/components/VoiceConfigEditor';
import CheckboxInput from './CheckboxInput';
import CollapsibleSection from './CollapsibleSection';
import Voices from '../../../utils/google-tts-voices';
import AssetSelectInput from './AssetSelectInput';

export default {
  components: {
    VoiceConfigEditor,
    CheckboxInput,
    CollapsibleSection,
    AssetSelectInput,
  },
  props: {
    text: { type: String },
    dataObject: { type: Object },
    fieldName: { type: String },
    character: { type: String }, // optional
  },
  data() {
    return {
      voiceOptions: Voices.map(voice => ({
        value: voice.name,
        display: voice.display,
      })),
      loaded: false,
      voiceLine: {
        locale: 'en-US',
        generatorType: 'tts-google',
        recordedText: '',
        audioUrl: '',
        googleTtsAudioConfig: {
          speakingRate: 1,
          pitch: 0,
          volumeGainDb: 0,
        },
        voice: Voices[0].name,
        preventBulkGenerationUpdating: false,
        preventBulkGenerationUpdatingVoice: false,
      },
      customVoiceLine: '',
    };
  },
  computed: {
    collapsibleId() {
      // TODO: might be better to just have a prop, but didn't want to change every usage of this component

      let dataObjectName = this.dataObject && (this.dataObject.name || this.dataObject.title || this.dataObject.text);
      if (dataObjectName) {
        dataObjectName = dataObjectName.replace(/\s/g, '');
      } else {
        dataObjectName = 'noname';
      }

      return `TTS-${
        (this.dataObject && this.dataObject._id) || 'noid'
      }-${
        dataObjectName
      }-${
        this.fieldName
      }`;
    },
    /** ObjectID of existing voice line (or null/undefined if there is none) */
    existingVoiceLine() {
      return this.dataObject && this.dataObject[this.fieldName];
    },
    filteredText() {
      return this.text ? this.text.replace(/<br>/g, ' ').replace(/<\/?\w[^>]*(>|$)/g, '') : 'No Text';
    },
    truncatedText() {
      if (!this.filteredText || this.filteredText.trim().length === 0) return 'No Text';

      const len = 15;
      return this.filteredText && this.filteredText.length > len ? `${this.filteredText.slice(0, len)}...` : this.filteredText;
    },
    preventBulkGenerationUpdating() {
      return this.voiceLine.preventBulkGenerationUpdating;
    },
    preventBulkGenerationUpdatingVoice() {
      return this.voiceLine.preventBulkGenerationUpdatingVoice;
    },
  },
  watch: {
    async existingVoiceLine(newValue, oldValue) {
      if (this.existingVoiceLine && (newValue != null) && (newValue !== oldValue)) {
        await this.loadVoiceLine();
      }
    },
    voiceLine(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue && newValue.generatorType === 'voice-actor') {
          this.customVoiceLine = newValue.audioUrl;
        } else {
          this.customVoiceLine = '';
        }
      }
    },
  },
  async mounted() {
    if (this.existingVoiceLine) {
      await this.loadVoiceLine();
    }
  },
  methods: {
    ...mapMutations([
      'submitEditUpdate',
    ]),
    ...mapActions([
      'flashSuccess',
      'createVoiceLine',
      'getVoiceLine',
      'updateVoiceLine',
      'lockVoiceLine',
      'lockVoiceLineVoiceSettings',
      'deleteVoiceLine',
      'playVoice'
    ]),
    async loadVoiceLine() {
      const result = await this.getVoiceLine(this.existingVoiceLine);
      if (result) {
        this.voiceLine = result;
        this.loaded = true;
        return result;
      }
      return null;
    },
    async previewVoiceLine() {
      if (this.existingVoiceLine) {
        const result = await this.loadVoiceLine();
        if (result) {
          this.playVoice({ line: result });
        }
      }
    },
    async downloadVoiceLine() {
      if (this.existingVoiceLine) {
        const result = await this.loadVoiceLine();
        let url = result;
        if (result && result.audioUrl && typeof result.audioUrl === 'string') {
          url = result.audioUrl;
        } else if (!url || typeof result.audioUrl !== 'string') {
          console.warn('Failed to download voice line because it has no audioUrl', result);
          return;
        }
        const a = document.createElement('a');
        a.href = url;
        a.download = url.split('/').pop();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    },
    async generateOrOverwriteVoiceLine(clearCustomVoiceLine = false) {
      // remove all html tags and set text
      // TODO: don't remove tags if using SSML field
      this.voiceLine.recordedText = this.filteredText;
      // eslint-disable-next-line no-alert
      if (this.existingVoiceLine && confirm('Are you sure you want to overwrite this voice line?')) {
        try {
          if (clearCustomVoiceLine) {
            this.customVoiceLine = '';
            if (this.voiceLine) {
              this.voiceLine.generatorType = 'tts-google';
            }
          }
          const result = await this.updateVoiceLine({ id: this.existingVoiceLine, voiceLine: this.voiceLine });
          if (result) {
            this.voiceLine = result;
          }
        } catch (err) {
          // eslint-disable-next-line no-alert
          alert('Error updating voice line.  See console for details.');
          throw err;
        }
      } else if (!(this.existingVoiceLine)) {
        const result = await this.createVoiceLine(this.voiceLine);
        if (result) {
          this.voiceLine = result;
          if (this.dataObject && this.fieldName && this.voiceLine._id) {
            this.$set(this.dataObject, this.fieldName, this.voiceLine._id);
            this.submitEditUpdate();
          }
        }
      }
    },
    async generateButtonClicked() {
      await this.generateOrOverwriteVoiceLine(true);
    },
    async deleteExistingVoiceLine() {
      // eslint-disable-next-line no-alert
      if (this.existingVoiceLine && confirm('Are you sure you want to delete this voice line?')) {
        try {
          await this.deleteVoiceLine(this.existingVoiceLine);
        } finally {
          this.customVoiceLine = '';
          // reset the locally fetched voice line to default state
          this.voiceLine.recordedText = '';
          this.voiceLine.audioUrl = '';
          if (this.dataObject && this.fieldName) {
            this.dataObject[this.fieldName] = null;
            this.submitEditUpdate();
          }
        }
      }
    },
    async onCustomVoiceLineAssetSelected() {
      if (this.customVoiceLine !== this.voiceLine.audioUrl) {
        if (this.customVoiceLine && this.customVoiceLine.length > 0) {
          this.voiceLine.audioUrl = this.customVoiceLine;
          this.voiceLine.generatorType = 'voice-actor';
          this.voiceLine.preventBulkGenerationUpdating = true;
          this.voiceLine.preventBulkGenerationUpdatingVoice = true;
          await this.generateOrOverwriteVoiceLine();
        } else if (this.voiceLine.audioUrl) {
          this.deleteExistingVoiceLine();
        }
      }
    },
    async onInputPreventBulkGenerationUpdating() {
      if (this.existingVoiceLine && this.loaded && this.voiceLine) {
        try {
          await this.lockVoiceLine({
            id: this.existingVoiceLine,
            lock: this.voiceLine.preventBulkGenerationUpdating,
          });
          this.flashSuccess({ message: 'Voice Line setting saved' });
        } catch (err) {
          // eslint-disable-next-line no-alert
          alert('Error updating voice line.  See console for details.');
          throw err;
        }
      }
    },
    async onInputPreventBulkGenerationUpdatingVoice() {
      if (this.existingVoiceLine && this.loaded && this.voiceLine) {
        try {
          await this.lockVoiceLineVoiceSettings({
            id: this.existingVoiceLine,
            lock: this.voiceLine.preventBulkGenerationUpdatingVoice,
          });
          this.flashSuccess({ message: 'Voice Line setting saved' });
        } catch (err) {
          // eslint-disable-next-line no-alert
          alert('Error updating voice line.  See console for details.');
          throw err;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>



// WEBPACK FOOTER //
// src/components/ui/inputs/VoiceLineInput.vue